// Plans (spec §14.1): Free for one person, Team for collaborating. Shared so
// the server's permission checks and the client's plan page agree.

export type PlanId = 'free' | 'team';
export type BillingStatus = 'none' | 'trialing' | 'active' | 'past_due' | 'lapsed';

/** A workspace's plan state, as permission checks need it. */
export interface Billing {
  plan: PlanId;
  status: BillingStatus;
}

/**
 * What a plan state allows: `free` (only owners edit), `team` (roles apply
 * as normal: trialing, active or past due) or `read-only` (a lapsed Team).
 */
export type PlanMode = 'free' | 'team' | 'read-only';

export function planMode(b: Billing): PlanMode {
  if (b.plan !== 'team') return 'free';
  return b.status === 'lapsed' ? 'read-only' : 'team';
}

export const TRIAL_DAYS = 14;
export const TRIAL_REMINDER_DAYS = 3;

/** Team price per seat (a non-guest member), in USD. Stripe's prices must match. */
export const TEAM_PRICE = { month: 6, year: 60, currency: 'USD' } as const;
export type BillingInterval = keyof Pick<typeof TEAM_PRICE, 'month' | 'year'>;

export const formatPrice = (amount: number) => `$${amount}`;

/** Images and audio a Free workspace's boards can hold (spec §19.6); Team has no limit. FREE_STORAGE_MB overrides it. */
export const FREE_STORAGE_BYTES = 1024 ** 3;

/** 125829120 → "120 MB"; 1073741824 → "1 GB". */
export function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${+(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.ceil(n / 1024)} KB`;
}
